import { useEffect, useRef, useState } from "react"
import {
  aAnsweredQuestionIndices,
  aBlockingQuestion,
  aIsPlaying,
  aSentimentAnswers,
  useAtom,
  useAtomValue,
  useSetAtom,
} from "~/atoms"
import type { SentimentQuestion } from "~/types"
import { cn } from "~/utils"

type AnswerType = "rating" | "text"

function getAnswerType(options: { value: string }[]): AnswerType {
  const values = options.map((o) => o.value)
  const numericValues = values.map((v) => parseInt(v, 10))
  if (
    values.length === 5 &&
    numericValues.every((n, i) => !isNaN(n) && n === i + 1)
  )
    return "rating"
  return "text"
}

const RATING_EMOJIS = ["😞", "😕", "😐", "🙂", "😊"]

// ─── Shell styles ─────────────────────────────────────────────────────────────

type ShellStyle = {
  label: string
  overlay: string
  card: string
  question: string
}

const SHELL_STYLES: ShellStyle[] = [
  {
    label: "Nametag",
    overlay: "bg-black/70 backdrop-blur-sm",
    card: "max-w-sm w-full max-h-full bg-zinc-900 border border-white/10 overflow-y-auto rounded-xl shadow-2xl px-3 py-4 sm:px-6 sm:py-5",
    question: "mb-5 text-center text-sm font-bold leading-snug text-white",
  },
]

// ─── Rating answer components ─────────────────────────────────────────────────

type RatingProps = {
  options: { value: string }[]
  onSelect: (v: string) => void
}

function RatingEmoji({ options, onSelect }: RatingProps) {
  return (
    <div className="flex justify-center gap-3">
      {options.map((opt, i) => (
        <button
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          aria-label={`Rating ${i + 1}`}
          className="rounded-full p-1 text-2xl leading-none transition-transform hover:scale-125 focus:outline-none sm:text-4xl"
        >
          {RATING_EMOJIS[i]}
        </button>
      ))}
    </div>
  )
}


// ─── Text answer components ───────────────────────────────────────────────────

type TextProps = { options: { value: string }[]; onSelect: (v: string) => void }

function TextFloating({ options, onSelect }: TextProps) {
  return (
    <div className="grid w-full grid-cols-2 gap-2">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          className="rounded-2xl bg-white/10 px-3 py-3 text-center text-sm text-white shadow-[0_4px_12px_rgba(0,0,0,0.3)] backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white/20 hover:shadow-[0_6px_20px_rgba(0,0,0,0.4)] focus:outline-none"
        >
          {opt.value}
        </button>
      ))}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SentimentQuestions() {
  const blockingQuestion = useAtomValue(aBlockingQuestion)
  const [isPlaying, setIsPlaying] = useAtom(aIsPlaying)
  const setAnsweredIndices = useSetAtom(aAnsweredQuestionIndices)
  const setSentimentAnswers = useSetAtom(aSentimentAnswers)
  const wasPlayingRef = useRef(false)

  const [displayedQuestion, setDisplayedQuestion] = useState<{
    question: SentimentQuestion
    index: number
  } | null>(null)
  const [showThanks, setShowThanks] = useState(false)

  useEffect(() => {
    if (blockingQuestion) setDisplayedQuestion(blockingQuestion)
  }, [blockingQuestion])

  useEffect(() => {
    if (blockingQuestion) {
      wasPlayingRef.current = isPlaying
      if (isPlaying) setIsPlaying(false)
    }
  }, [blockingQuestion, isPlaying, setIsPlaying])

  useEffect(() => {
    if (blockingQuestion) {
      const { id, question } = blockingQuestion.question
      setSentimentAnswers((prev) => ({
        ...prev,
        [id]: { ...prev[id], question, status: "shown" },
      }))
    }
  }, [blockingQuestion, setSentimentAnswers])

  const handleAnswer = (value: string, questionIndex: number) => {
    setAnsweredIndices((prev: Set<number>) => {
      const next = new Set(prev)
      next.add(questionIndex)
      return next
    })
    if (displayedQuestion) {
      const { id, question } = displayedQuestion.question
      setSentimentAnswers((prev) => ({
        ...prev,
        [id]: { answer: value, question, status: "answered" },
      }))
    }
    if (wasPlayingRef.current) setIsPlaying(true)
    setShowThanks(true)
    setTimeout(() => {
      setShowThanks(false)
      setDisplayedQuestion(null)
    }, 3000)
  }

  const isVisible = !!blockingQuestion
  const question = displayedQuestion?.question
  const index = displayedQuestion?.index ?? 0
  const answerType = question ? getAnswerType(question.options) : null
  const shell = SHELL_STYLES[0]

  return (
    <div
      className={cn(
        "absolute inset-0 z-20 flex flex-col items-center justify-center overflow-y-auto p-4 transition-opacity duration-300 sm:p-8",
        shell.overlay,
        isVisible || showThanks ? "opacity-100" : "pointer-events-none opacity-0"
      )}
      data-disable-control="true"
    >
      {showThanks ? (
        <p className="text-sm font-medium text-white/80 sm:text-base">
          Thanks for your response
        </p>
      ) : (
        question && (
          <div className={shell.card}>
            <h2 className={shell.question}>{question.question}</h2>
            {answerType === "rating" && (
              <RatingEmoji
                options={question.options}
                onSelect={(v) => handleAnswer(v, index)}
              />
            )}
            {answerType === "text" && (
              <TextFloating
                options={question.options}
                onSelect={(v) => handleAnswer(v, index)}
              />
            )}
          </div>
        )
      )}
    </div>
  )
}
